import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Drawer, DrawerTabBar, Select, ToolbarSearch } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { usePipelines, useCreatePipeline, useUpdatePipeline, useDeletePipelineWithValidation, usePipelineDeleteValidation } from '../hooks/usePipelines';
import { useStagesByPipelineId } from '../hooks/useStages';
import type {
  TicketPipelineResponse, DistributionConfig, TicketPipelineDeleteValidation,
  SlaPolicyQuery, SlaPolicyResponse,
} from '@troia-v3/types';
import { AssignmentConfigSection } from '@/shared/components/assignment';
import { ContextUsersTab } from '@/shared/components/context-users';
import { usePermissions } from '@/hooks/usePermissions';
import { useFetchFreshSlaPolicy, useSlaPolicies, useUpdateSlaPolicy } from '@/modules/sla-policies/hooks';
import { policiesContainingPipeline } from '@/modules/sla-policies/utils/slaPolicyHelpers';
import { Icon } from '@/shared/icons';
import { StagesManager } from './StagesManager';
import { PipelineAlertsTab } from './PipelineAlertsTab';
import { buildPipelineDeleteConfirm } from './pipelineDeleteConfirm';

/**
 * Drawer lateral Master-Detail para configuração de Pipelines do Helpdesk.
 *
 * Substitui a antiga modal central de configuração (views empilhadas
 * list→create→edit→manage-stages, já removida) pelo mesmo layout do `FunnelsSettingsDrawer`
 * do CRM: lista de pipelines sempre visível à esquerda (280px) + detalhe com
 * abas à direita, 880px.
 *
 * O que é só do Helpdesk sobrevive à migração: categoria de status por etapa
 * (que governa a pausa do relógio de SLA), preview de etapas no item da lista e
 * exclusão validada. O que é só do CRM — tipos de lead, roteamento, leads
 * duplicados — NÃO é transplantado (spec SLA §9.1).
 *
 * Props idênticas às da modal que ela substitui: a troca no `TicketsPage` é
 * substituição de nome, sem adaptador.
 *
 * @example
 * <PipelinesSettingsDrawer isOpen={isOpen} onClose={() => setIsOpen(false)} />
 */
export interface PipelinesSettingsDrawerProps {
  /** Drawer open state */
  isOpen: boolean;
  /** Callback when the drawer should close */
  onClose: () => void;
}

/**
 * Abas do painel de detalhe. As cinco estão no array e com painel plugado:
 * `stages` (`StagesManager`, C7) e `alerts` (`PipelineAlertsTab`, C10) entraram
 * junto com seus componentes — listar aba sem painel entregaria aba vazia.
 *
 * Não há aba de tipos, roteamento nem duplicados: são exclusivas do CRM e não
 * têm equivalente em pipeline (spec SLA §9.1).
 */
export type PipelineDrawerTab = 'basic' | 'stages' | 'alerts' | 'distribution' | 'users';

const TAB_LABELS: Record<PipelineDrawerTab, string> = {
  basic: 'Básico',
  stages: 'Etapas',
  alerts: 'Alertas',
  distribution: 'Distribuição',
  users: 'Usuários',
};

interface PipelineFormState {
  name: string;
  description: string;
  color: string;
  assignmentConfig?: DistributionConfig;
  /** Id da política de SLA a vincular. '' = nenhuma. NÃO é campo de
   *  `TicketPipeline` — é o rascunho do vínculo, gravado em `sla-policies`. */
  slaPolicyId: string;
}

const EMPTY_FORM: PipelineFormState = {
  name: '',
  description: '',
  color: '#8b5cf6',
  slaPolicyId: '',
};

export const PipelinesSettingsDrawer: React.FC<PipelinesSettingsDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<PipelineDrawerTab>('basic');
  const [pipelineForm, setPipelineForm] = useState<PipelineFormState>(EMPTY_FORM);

  // `enabled: isOpen` é obrigatório: o drawer fica sempre montado no TicketsPage
  // (preserva a animação de saída de 300ms do Drawer), então sem o gate a lista
  // seria buscada com a tela fechada. `limit: 100` corrige o truncamento
  // silencioso de `limit: 20` da antiga modal (já removida).
  const { data: pipelinesData, isLoading } = usePipelines({ limit: 100 }, isOpen);
  const createPipeline = useCreatePipeline();
  const updatePipeline = useUpdatePipeline();
  const confirm = useConfirm();
  const deletePipeline = useDeletePipelineWithValidation();
  const validateDeletion = usePipelineDeleteValidation();

  const pipelines = useMemo(() => pipelinesData?.items ?? [], [pipelinesData]);

  const filteredPipelines = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pipelines;
    return pipelines.filter((p) => p.name.toLowerCase().includes(q));
  }, [pipelines, search]);

  const selectedPipeline = useMemo(
    () => pipelines.find((p) => p.id === selectedPipelineId) ?? null,
    [pipelines, selectedPipelineId],
  );

  const { hasModulePermission } = usePermissions();
  /* Sem `sla-policies:read` a lista volta 403 e sem `:update` o PUT é recusado
     pelo servidor. Nesse caso o campo simplesmente não aparece — mostrar um
     Select que erra ao salvar é pior que não mostrar nada. */
  const canLinkSlaPolicy =
    hasModulePermission('sla-policies', 'read') && hasModulePermission('sla-policies', 'update');

  /* Só políticas ATIVAS de chamado: vincular funil a política inativa grava um
     escopo que o motor nunca lê (`findActiveForCompany`, B3). `limit: 100` é o
     mesmo teto da tela de políticas (C17, decisão 2).
     A query é `undefined` com o drawer fechado ou sem permissão — hook de fetch
     em componente com `isOpen` tem que ser condicionado (CLAUDE.md NUNCA #14). */
  const slaPolicyQuery: SlaPolicyQuery | undefined = useMemo(
    () =>
      isOpen && canLinkSlaPolicy
        ? { page: 1, limit: 100, filters: { entityType: 'ticket', status: 'active' } }
        : undefined,
    [isOpen, canLinkSlaPolicy],
  );
  const { data: slaPoliciesData } = useSlaPolicies(slaPolicyQuery);
  // `silent: true`: `syncSlaPolicyLink` (abaixo) faz até 2 PUTs nesta mutation
  // para 1 clique de "Salvar" — os toasts automáticos da mutation ficariam
  // duplicados/genéricos demais; quem decide o que mostrar (e se mostra) é o
  // próprio `syncSlaPolicyLink` (Rodada 1 de conserto, C21).
  const updateSlaPolicy = useUpdateSlaPolicy({ silent: true });
  const fetchFreshSlaPolicy = useFetchFreshSlaPolicy();
  const [isSyncingSlaPolicy, setIsSyncingSlaPolicy] = useState(false);

  const slaPolicies = useMemo(() => slaPoliciesData?.items ?? [], [slaPoliciesData]);

  /**
   * Política vinculada HOJE a este funil. `useSlaPolicies` já entrega
   * `slaPolicies` na ordem de AVALIAÇÃO do motor (`sortPoliciesForEvaluation`,
   * aplicada via `select` na própria hook — Rodada 1 de conserto, C21), então
   * `find` devolve a primeira que casa — a MESMA que `resolvePolicy`
   * (backend) escolheria. **Antes da Rodada 1 isto era falso**: nem a hook
   * nem `slaPoliciesApi.list` mandavam `sort`, e o servidor defaultava pra
   * `createdAt: -1` — este `find` podia devolver a política mais RECENTE em
   * vez da que de fato vence a cascata, e trocar o vínculo a partir dali
   * podia deixar o mesmo funil em duas políticas ao mesmo tempo.
   *
   * Nada impede duas políticas de listarem o mesmo funil no escopo (o
   * servidor não proíbe, e é legítimo: escopos podem se sobrepor por
   * prioridade/canal); o campo mostra a que vence.
   */
  const linkedSlaPolicy: SlaPolicyResponse | undefined = useMemo(
    () =>
      selectedPipeline
        ? slaPolicies.find((p) => (p.scope.pipelineIds ?? []).includes(selectedPipeline.id))
        : undefined,
    [slaPolicies, selectedPipeline],
  );

  // `handleSelectPipeline` grava `slaPolicyId: ''` como placeholder (a lista
  // de políticas pode chegar depois da seleção) — este efeito corrige assim
  // que `linkedSlaPolicy` resolve.
  useEffect(() => {
    setPipelineForm((prev) => ({ ...prev, slaPolicyId: linkedSlaPolicy?.id ?? '' }));
  }, [linkedSlaPolicy]);

  // Reset a cada abertura — a antiga modal (já removida) fazia o equivalente
  // com `setView('list')`.
  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setActiveTab('basic');
    setIsCreating(false);
    setSelectedPipelineId(null);
    setPipelineForm(EMPTY_FORM);
  }, [isOpen]);

  const handleSelectPipeline = (pipeline: TicketPipelineResponse) => {
    setIsCreating(false);
    setSelectedPipelineId(pipeline.id);
    setPipelineForm({
      name: pipeline.name,
      description: pipeline.description || '',
      color: pipeline.color,
      assignmentConfig: pipeline.assignmentConfig,
      // Placeholder até a lista de políticas resolver — o efeito abaixo
      // (dependente de `linkedSlaPolicy`) corrige assim que ela chegar.
      slaPolicyId: '',
    });
    setActiveTab('basic');
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setSelectedPipelineId(null);
    setPipelineForm(EMPTY_FORM);
    setActiveTab('basic');
  };

  const handleCreatePipeline = async () => {
    try {
      const created = await createPipeline.mutateAsync({
        name: pipelineForm.name,
        description: pipelineForm.description,
        color: pipelineForm.color,
        order: pipelines.length,
        assignmentConfig: pipelineForm.assignmentConfig,
      });
      setIsCreating(false);
      // Cai direto na edição do recém-criado — é o que habilita as abas que
      // exigem id (Usuários, e depois Etapas/Alertas).
      handleSelectPipeline(created);
    } catch {
      // toast tratado pelo hook (`useCreatePipeline.onError`, usePipelines.ts)
    }
  };

  /**
   * Move este funil de uma política para outra. `nextPolicyId` vazio =
   * "Nenhuma": só a(s) remoção(ões) acontece(m), e o funil passa a cair na
   * política padrão (a última da cascata, B4) como qualquer funil sem escopo
   * próprio — nunca em "sem SLA".
   *
   * Rodada 2 de conserto (C21) — generaliza pra dado LEGADO: nada no backend
   * impede duas (ou mais) políticas de listarem o mesmo pipeline no escopo ao
   * mesmo tempo. Até aqui o sync só sabia limpar a "vencedora"
   * (`linkedSlaPolicy`, a primeira na ordem de avaliação) — se uma
   * "perdedora" também continha o pipeline, ela ficava intocada, e o motor
   * (`resolvePolicy`) continuava aplicando o que a ORDEM manda, não o que o
   * campo mostrava. Agora: `policiesContainingPipeline(slaPolicies, pipelineId)`
   * encontra TODAS (já na ordem de avaliação, herdada de `slaPolicies`), o
   * sync remove o pipeline de TODAS as que não são a escolhida, e só então
   * adiciona na escolhida — nessa ordem (remove tudo primeiro), pelo mesmo
   * motivo de sempre: não deixar o pipeline em duas políticas na janela entre
   * escritas.
   *
   * **O caso comum (0 ou 1 política contendo) degenera exatamente pro
   * comportamento da Rodada 1 — sem PUT extra**: 0 contendo + nada escolhido
   * = no-op; 0 contendo + algo escolhido = só o PUT de adição; 1 contendo e é
   * a escolhida = no-op; 1 contendo e é outra/"Nenhuma" = 1 PUT de remoção +
   * (opcional) 1 PUT de adição. Só quando 2+ políticas contêm o pipeline é
   * que aparecem PUTs de remoção extras — e mesmo aí, se a escolhida já
   * estiver entre elas, ela não é retocada (sem PUT redundante).
   *
   * Herdado da Rodada 1 (continua valendo):
   * 1. **Não engole falha parcial.** Cada remoção tem seu próprio
   *    `try/catch` — best-effort: uma remoção falhando não impede tentar as
   *    demais (limpa o máximo possível). A adição só roda se TODAS as
   *    remoções tentadas tiverem sucesso. Em qualquer falha, o Select volta a
   *    refletir o estado REAL: quem ainda contém o pipeline — a primeira
   *    política (na ordem de avaliação) entre "a escolhida, se já continha e
   *    por isso nunca foi tocada" e "as que falharam em sair" — não o que o
   *    usuário tinha acabado de escolher.
   * 2. **Escreve sobre `scope` FRESCO** (`fetchFreshSlaPolicy`, `staleTime: 0`,
   *    mesmo padrão de `usePipelineDeleteValidation`, C8) — nunca o cache de
   *    30s de `slaPolicies`. CAS de verdade é responsabilidade do backend
   *    (`SlaPoliciesService.update` não versiona `scope` hoje) — não
   *    perseguido aqui, residual conhecido, registrado no report.
   * 3. **No máximo 1 toast, e o "Salvar" espera o sync inteiro.** A mutation
   *    é `silent` (não emite os toasts automáticos da C16) — só ESTE handler
   *    decide se mostra algo. `isSyncingSlaPolicy` mantém o spinner do botão
   *    "Salvar" aceso durante todos os PUTs desta função.
   */
  const syncSlaPolicyLink = async (pipelineId: string, nextPolicyId: string): Promise<void> => {
    if (!canLinkSlaPolicy) return;

    const containing = policiesContainingPipeline(slaPolicies, pipelineId);
    const alreadyLinkedToChosen = !!nextPolicyId && containing.some((p) => p.id === nextPolicyId);

    // No-op: exatamente os dois cenários em que nada precisa mudar. Qualquer
    // outra combinação (inclusive 2+ contendo e a escolhida já sendo uma
    // delas — dado legado) segue adiante pra limpar as demais.
    if (containing.length === 0 && !nextPolicyId) return;
    if (containing.length === 1 && alreadyLinkedToChosen) return;

    // Quem perde o vínculo: todas as que contêm, exceto a escolhida (se ela
    // já contiver, não é retocada — sem PUT redundante).
    const toRemoveFrom = containing.filter((p) => p.id !== nextPolicyId);

    setIsSyncingSlaPolicy(true);
    try {
      const failedRemovals: typeof containing = [];
      for (const policy of toRemoveFrom) {
        try {
          const fresh = await fetchFreshSlaPolicy(policy.id);
          await updateSlaPolicy.mutateAsync({
            id: fresh.id,
            data: {
              scope: {
                ...fresh.scope,
                pipelineIds: (fresh.scope.pipelineIds ?? []).filter((id) => id !== pipelineId),
              },
            },
          });
        } catch {
          failedRemovals.push(policy);
        }
      }

      if (failedRemovals.length > 0) {
        // Nem toda remoção passou — a adição não roda (evita deixar o
        // pipeline em duas políticas). Estado real: quem ainda contém o
        // pipeline é quem falhou em sair, mais a escolhida se ela já
        // continha (nunca tocada). `containing` já vem na ordem de
        // avaliação — o primeiro dessa lista que ainda está vinculado é
        // quem `resolvePolicy` escolheria de fato.
        const stillLinkedIds = new Set(failedRemovals.map((p) => p.id));
        if (alreadyLinkedToChosen) stillLinkedIds.add(nextPolicyId);
        const realWinner = containing.find((p) => stillLinkedIds.has(p.id));

        toast.error('Não foi possível atualizar o vínculo de SLA. Tente novamente.');
        setPipelineForm((prev) => ({ ...prev, slaPolicyId: realWinner?.id ?? '' }));
        return;
      }

      if (nextPolicyId && !alreadyLinkedToChosen) {
        try {
          const freshNext = await fetchFreshSlaPolicy(nextPolicyId);
          await updateSlaPolicy.mutateAsync({
            id: freshNext.id,
            data: {
              scope: {
                ...freshNext.scope,
                // Set implícito: defensivo — a esta altura todas as outras
                // já foram limpas, mas não custa evitar duplicata no array.
                pipelineIds: Array.from(new Set([...(freshNext.scope.pipelineIds ?? []), pipelineId])),
              },
            },
          });
        } catch {
          // Adição falhou DEPOIS de todas as remoções terem sucesso — o
          // pipeline agora está SEM NENHUMA política (se havia `toRemoveFrom`)
          // ou nunca chegou a ter uma (se não havia nada pra remover). Não
          // pode passar por "sucesso" silencioso (achado 1 da Rodada 1).
          toast.warning(
            toRemoveFrom.length > 0
              ? 'O pipeline ficou sem política de SLA — selecione novamente.'
              : 'Não foi possível vincular a política de SLA. Tente novamente.',
          );
          setPipelineForm((prev) => ({ ...prev, slaPolicyId: '' }));
        }
      }
    } finally {
      setIsSyncingSlaPolicy(false);
    }
  };

  const handleEditPipeline = async () => {
    if (!selectedPipeline) return;
    try {
      await updatePipeline.mutateAsync({
        id: selectedPipeline.id,
        data: {
          name: pipelineForm.name,
          description: pipelineForm.description,
          color: pipelineForm.color,
          assignmentConfig: pipelineForm.assignmentConfig,
        },
      });
      await syncSlaPolicyLink(selectedPipeline.id, pipelineForm.slaPolicyId);
    } catch {
      // toast tratado pelo hook (`useUpdatePipeline.onError`, usePipelines.ts)
    }
  };

  const handleDeletePipeline = async (pipeline: TicketPipelineResponse) => {
    // NUNCA window.confirm — o modal do ConfirmProvider é promise-based.
    // Pergunta ao backend ANTES de perguntar ao usuário: confirmar uma exclusão
    // que já se sabe impossível é o defeito da modal antiga.
    let validation: TicketPipelineDeleteValidation | null = null;
    try {
      validation = await validateDeletion(pipeline.id);
    } catch {
      validation = null; // rede/500 — o diálogo admite a incerteza
    }

    const decision = buildPipelineDeleteConfirm(pipeline.name, validation);
    const confirmed = await confirm(decision.options);
    if (!confirmed || !decision.canProceed) return;

    try {
      await deletePipeline.mutateAsync(pipeline.id);
      if (selectedPipelineId === pipeline.id) {
        setSelectedPipelineId(null);
        setPipelineForm(EMPTY_FORM);
        setActiveTab('basic');
      }
    } catch {
      // toast tratado pelo hook (`useDeletePipelineWithValidation.onError`, usePipelines.ts)
    }
  };

  const handleClose = () => {
    setSelectedPipelineId(null);
    setIsCreating(false);
    setActiveTab('basic');
    setSearch('');
    setPipelineForm(EMPTY_FORM);
    onClose();
  };

  // Mobile (colunas empilhadas): sai do detalhe de volta pra lista.
  const handleBackToList = () => {
    setSelectedPipelineId(null);
    setIsCreating(false);
    setActiveTab('basic');
    setPipelineForm(EMPTY_FORM);
  };

  const handleCancel = () => {
    if (isCreating) {
      setIsCreating(false);
      setPipelineForm(EMPTY_FORM);
      setActiveTab('basic');
    } else {
      handleClose();
    }
  };

  const showDetail = isCreating || !!selectedPipeline;

  const subtitle = isCreating
    ? 'Criando novo pipeline'
    : selectedPipeline
      ? `Editando ${selectedPipeline.name}`
      : 'Selecione um pipeline à esquerda';

  const tabs: PipelineDrawerTab[] = isCreating || !selectedPipeline
    ? ['basic', 'stages', 'distribution']
    : ['basic', 'stages', 'alerts', 'distribution', 'users'];

  /* Rodapé suprimido nas abas donas do próprio salvar: 'stages' salva etapa a
     etapa pelas mutations do StagesManager (C7) e 'alerts' tem botão próprio
     (C10). Mesma regra do drawer de funis (FunnelsSettingsDrawer.tsx:298). */
  const footer = showDetail && activeTab !== 'stages' && activeTab !== 'alerts' ? (
    <>
      <Button variant="secondary" onClick={handleCancel} data-testid="pipelines-drawer-cancel">
        Cancelar
      </Button>
      <Button
        variant="primary"
        onClick={isCreating ? handleCreatePipeline : handleEditPipeline}
        disabled={!pipelineForm.name.trim()}
        // `isSyncingSlaPolicy` (Rodada 1 de conserto, C21): `syncSlaPolicyLink`
        // roda DEPOIS de `updatePipeline.mutateAsync` resolver, dentro do
        // mesmo `handleEditPipeline` — sem este flag o spinner sumia assim
        // que o PUT do pipeline voltava, com os PUTs de `sla-policies` ainda
        // em voo (o clique parecia concluído antes de estar).
        loading={isCreating ? createPipeline.isPending : (updatePipeline.isPending || isSyncingSlaPolicy)}
        data-testid="pipelines-drawer-save"
      >
        {isCreating ? 'Criar Pipeline' : 'Salvar Alterações'}
      </Button>
    </>
  ) : undefined;

  return (
    /* Header padrão do Drawer (título + subtítulo + ícone). A prop `tabs` do
       Drawer NÃO se aplica: em master-detail as abas pertencem ao painel da
       direita, não ao header full-width. */
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      widthClass="w-[880px]"
      icon={<Icon name="columns" className="w-5 h-5" />}
      title="Configuração de Pipelines"
      subtitle={subtitle}
      {...(footer ? { footer } : {})}
    >
      <div className="flex h-full min-h-0" data-testid="pipelines-drawer">
        {/* Coluna esquerda — lista de pipelines (master). No mobile as colunas
            empilham (lista OU detalhe, nunca as duas espremidas): com detalhe
            ativo a lista some e o painel ganha um "voltar". */}
        <div
          className={`${showDetail ? 'hidden lg:flex' : 'flex'} w-full lg:w-[280px] flex-shrink-0 flex-col lg:border-r min-h-0`}
          style={{ borderColor: '#e5e7eb', backgroundColor: '#fcfcfd' }}
        >
          <div className="flex-shrink-0 p-3 pb-0">
            <Button
              variant="primary"
              icon="plus"
              fullWidth
              onClick={handleStartCreate}
              data-testid="pipelines-drawer-new"
            >
              Novo pipeline
            </Button>
          </div>

          <div className="flex-shrink-0 p-3 pb-2">
            {/* Busca canônica (38px, ícone 16, X de limpar) — a modal não tinha
                busca nenhuma; é ganho da migração, não paridade. */}
            <ToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Buscar pipeline…"
              data-testid="pipelines-drawer-search"
            />
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
            {isCreating && (
              <div
                className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg mb-1"
                style={{ border: '1px dashed #ddd6fe', backgroundColor: '#faf9ff' }}
                data-testid="pipelines-drawer-draft-item"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: pipelineForm.color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate" style={{ color: '#7c3aed' }}>
                    {pipelineForm.name || 'Novo pipeline'}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                    Rascunho
                  </div>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="text-center py-8 text-sm" style={{ color: '#64748b' }}>
                Carregando pipelines...
              </div>
            ) : filteredPipelines.length === 0 ? (
              !isCreating && (
                <div className="text-center py-8 text-sm px-2" style={{ color: '#94a3b8' }}>
                  Nenhum pipeline encontrado.
                </div>
              )
            ) : (
              filteredPipelines.map((pipeline) => (
                <PipelineListItem
                  key={pipeline.id}
                  pipeline={pipeline}
                  isSelected={!isCreating && selectedPipelineId === pipeline.id}
                  onSelect={() => handleSelectPipeline(pipeline)}
                  onDelete={() => handleDeletePipeline(pipeline)}
                />
              ))
            )}
          </div>
        </div>

        {/* Coluna direita — detalhe */}
        <div className={`${showDetail ? 'flex' : 'hidden lg:flex'} flex-1 flex-col min-w-0 min-h-0`}>
          {!showDetail ? (
            <div
              className="flex-1 flex items-center justify-center text-sm text-center px-6"
              style={{ color: '#94a3b8' }}
            >
              Selecione um pipeline à esquerda
            </div>
          ) : (
            <>
              {/* Mobile: volta para a lista de pipelines (colunas empilhadas) */}
              <button
                type="button"
                onClick={handleBackToList}
                className="lg:hidden flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b flex-shrink-0"
                style={{ color: '#7c3aed', borderColor: '#e5e7eb' }}
                data-testid="pipelines-drawer-back"
              >
                <Icon name="chevron-left" className="w-4 h-4" />
                Pipelines
              </button>
              <DrawerTabBar
                items={tabs.map((tab) => ({
                  id: tab,
                  label: TAB_LABELS[tab],
                  testid: `pipelines-drawer-tab-${tab}`,
                }))}
                active={activeTab}
                onChange={(id) => setActiveTab(id as PipelineDrawerTab)}
                data-testid="pipelines-drawer-tabs"
              />
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                  {activeTab === 'basic' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                          Nome do Pipeline
                        </label>
                        <Input
                          type="text"
                          value={pipelineForm.name}
                          onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
                          placeholder="Ex: Suporte, Onboarding, Desenvolvimento"
                          data-testid="pipelines-drawer-field-name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                          Descrição (Opcional)
                        </label>
                        <Textarea
                          value={pipelineForm.description}
                          onChange={(e) => setPipelineForm({ ...pipelineForm, description: e.target.value })}
                          rows={3}
                          resize="none"
                          placeholder="Descreva o propósito deste pipeline"
                          data-testid="pipelines-drawer-field-description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                          Cor do Pipeline
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={pipelineForm.color}
                            onChange={(e) => setPipelineForm({ ...pipelineForm, color: e.target.value })}
                            className="w-12 h-12 rounded cursor-pointer border"
                            style={{ borderColor: '#d1d5db' }}
                            data-testid="pipelines-drawer-field-color-picker"
                          />
                          <div className="flex-1">
                            <Input
                              type="text"
                              value={pipelineForm.color}
                              onChange={(e) => setPipelineForm({ ...pipelineForm, color: e.target.value })}
                              className="font-mono"
                              placeholder="#8b5cf6"
                              data-testid="pipelines-drawer-field-color-hex"
                            />
                          </div>
                        </div>
                      </div>

                      {canLinkSlaPolicy && (
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                            Política de SLA
                          </label>
                          <Select
                            value={pipelineForm.slaPolicyId}
                            onChange={(value) => setPipelineForm({ ...pipelineForm, slaPolicyId: value })}
                            disabled={!selectedPipeline}
                            options={[
                              { value: '', label: 'Nenhuma (usa a política padrão)' },
                              ...slaPolicies.map((p) => ({ value: p.id, label: p.name })),
                            ]}
                            data-testid="pipelines-drawer-field-sla-policy"
                          />
                          <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                            {selectedPipeline
                              ? 'Atalho para o escopo da política — o vínculo é gravado em Políticas de SLA. "Nenhuma" faz este funil cair na política padrão.'
                              : 'Salve o pipeline para poder vincular uma política.'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'stages' && (
                    selectedPipeline ? (
                      <StagesManager pipelineId={selectedPipeline.id} />
                    ) : (
                      <div className="text-center py-8 text-sm" style={{ color: '#94a3b8' }}>
                        Salve o pipeline primeiro para gerenciar as etapas.
                      </div>
                    )
                  )}

                  {activeTab === 'alerts' && selectedPipeline && (
                    <PipelineAlertsTab key={selectedPipeline.id} pipeline={selectedPipeline} />
                  )}

                  {activeTab === 'distribution' && (
                    <AssignmentConfigSection
                      config={pipelineForm.assignmentConfig ?? { enabled: false, type: 'sequential' }}
                      onChange={(config) => setPipelineForm({ ...pipelineForm, assignmentConfig: config })}
                      resourceType="pipeline"
                      {...(selectedPipeline?.id ? { resourceId: selectedPipeline.id } : {})}
                      variant="inline"
                    />
                  )}

                  {activeTab === 'users' && selectedPipeline && (
                    <ContextUsersTab contextRef={{ kind: 'pipeline', id: selectedPipeline.id }} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

PipelinesSettingsDrawer.displayName = 'PipelinesSettingsDrawer';

// ============================================================================
// PipelineListItem — item da lista master (coluna esquerda)
// ============================================================================

/** Quantos pontos de etapa cabem na tira antes do "+N". */
const STAGE_PREVIEW_LIMIT = 6;

interface PipelineListItemProps {
  pipeline: TicketPipelineResponse;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const PipelineListItem: React.FC<PipelineListItemProps> = ({ pipeline, isSelected, onSelect, onDelete }) => {
  // Uma query por item (molde do FunnelListItem). O custo é real e conhecido:
  // N requisições ao abrir o drawer. Mitigado por (a) a lista só montar com o
  // drawer aberto — o Drawer desmonta os filhos quando fechado (o
  // `if (!shouldRender) return null` de Drawer.tsx) — e (b) o `staleTime` de
  // 5min de `useStagesByPipelineId` (useStages.ts). Se a base
  // passar de algumas dezenas de pipelines por empresa, a saída é uma rota de
  // contagem agregada; não está neste plano.
  const { data: stages } = useStagesByPipelineId(pipeline.id);
  const stagesCount = stages?.length ?? 0;
  const previewStages = (stages ?? []).slice(0, STAGE_PREVIEW_LIMIT);
  const overflow = stagesCount - previewStages.length;

  return (
    <div
      onClick={onSelect}
      className="group flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg cursor-pointer mb-0.5 transition-colors"
      style={{ backgroundColor: isSelected ? '#f5f3ff' : 'transparent' }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = '#f4f4f7';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      data-testid={`pipelines-drawer-item-${pipeline.id}`}
    >
      <span
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: pipeline.color }}
      />
      <div className="min-w-0 flex-1">
        <div
          className="text-sm font-semibold truncate"
          style={{ color: isSelected ? '#7c3aed' : '#334155' }}
        >
          {pipeline.name}
        </div>
        {/* Preview de etapas — o que só existe no Helpdesk. Na antiga modal
            (já removida) ficava atrás de um acordeão; aqui é permanente e
            não custa clique. */}
        <div
          className="flex items-center gap-1.5 mt-1"
          data-testid={`pipelines-drawer-stages-preview-${pipeline.id}`}
        >
          {previewStages.map((stage) => (
            <span
              key={stage.id}
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: stage.color }}
            />
          ))}
          <span className="text-xs" style={{ color: '#94a3b8' }}>
            {stagesCount} {stagesCount === 1 ? 'etapa' : 'etapas'}
            {overflow > 0 ? ` (+${overflow})` : ''}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded transition-opacity flex-shrink-0"
        style={{ color: '#cbd5e1' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#fef2f2';
          e.currentTarget.style.color = '#dc2626';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#cbd5e1';
        }}
        data-testid={`pipelines-drawer-delete-${pipeline.id}`}
      >
        <Icon name="trash" className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
